import React, { useState, useEffect, useRef } from 'react';
import Icon from './Icon';

const OnboardingTooltip = ({
  content,
  position,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
  onExit,
  elementRect,
  isFirstStep,
  isLastStep
}) => {
  const tooltipRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [calculatedPosition, setCalculatedPosition] = useState(position);

  useEffect(() => {
    if (!elementRect || !tooltipRef.current) return;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    const padding = 12;
    let newPosition = position;
    let top = 0;
    let left = 0;

    const checkSpace = () => {
      const spaces = {
        bottom: viewport.height - elementRect.bottom - tooltipRect.height - padding > 0,
        top: elementRect.top - tooltipRect.height - padding > 0,
        right: viewport.width - elementRect.right - tooltipRect.width - padding > 0,
        left: elementRect.left - tooltipRect.width - padding > 0
      };

      if (position === 'auto' || !spaces[position]) {
        if (spaces.bottom) return 'bottom';
        if (spaces.top) return 'top';
        if (spaces.right) return 'right';
        if (spaces.left) return 'left';
        return 'bottom';
      }
      return position;
    };

    newPosition = checkSpace();
    setCalculatedPosition(newPosition);

    switch (newPosition) {
      case 'bottom':
        top = elementRect.bottom + padding;
        left = elementRect.left + (elementRect.width / 2) - (tooltipRect.width / 2);
        left = Math.max(padding, Math.min(left, viewport.width - tooltipRect.width - padding));
        break;

      case 'top':
        top = elementRect.top - tooltipRect.height - padding;
        left = elementRect.left + (elementRect.width / 2) - (tooltipRect.width / 2);
        left = Math.max(padding, Math.min(left, viewport.width - tooltipRect.width - padding));
        break;

      case 'right':
        top = elementRect.top + (elementRect.height / 2) - (tooltipRect.height / 2);
        top = Math.max(padding, Math.min(top, viewport.height - tooltipRect.height - padding));
        left = elementRect.right + padding;
        break;

      case 'left':
        top = elementRect.top + (elementRect.height / 2) - (tooltipRect.height / 2);
        top = Math.max(padding, Math.min(top, viewport.height - tooltipRect.height - padding));
        left = elementRect.left - tooltipRect.width - padding;
        break;

      default:
        break;
    }

    setTooltipPosition({ top, left });
  }, [elementRect, position]);

  if (!elementRect) return null;

  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div
      ref={tooltipRef}
      className="onboarding-tooltip"
      style={{
        position: 'fixed',
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        zIndex: 10001
      }}
    >
      <div className="onboarding-progress">
        <span>Step {currentStep + 1} of {totalSteps}</span>
        <div className="onboarding-progress-bar">
          <div
            className="onboarding-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="onboarding-title-wrapper">
        <Icon name="info-circled" size="1.25rem" className="onboarding-title-icon" />
        <h3 className="onboarding-title">{content.title}</h3>
      </div>
      <p className="onboarding-description">{content.description}</p>

      <div className="onboarding-actions">
        <div className="onboarding-actions-left">
          <button className="onboarding-btn onboarding-btn-skip" onClick={onSkip}>
            Skip Tour
          </button>
        </div>
        <div className="onboarding-actions-right">
          {!isFirstStep && (
            <button className="onboarding-btn onboarding-btn-previous" onClick={onPrevious}>
              Previous
            </button>
          )}
          <button className="onboarding-btn onboarding-btn-next" onClick={onNext}>
            {isLastStep ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTooltip;
